import React, { useState, useEffect } from 'react';
import { adminAuthService } from '../../services/adminAuth';
import { logsService } from '../../services/logsService';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../styles/UserManager.css';

const UserManager = () => {
  const { user } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('list');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserRoleIds, setEditingUserRoleIds] = useState([]);
  const [editingUserPermissions, setEditingUserPermissions] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    roleIds: [],
    customPermissions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [usersRes, rolesRes, permsRes] = await Promise.all([
      adminAuthService.getAllAdminUsers(),
      adminAuthService.getRoles(),
      adminAuthService.getPermissions()
    ]);

    if (usersRes.success) setUsers(usersRes.users);
    if (rolesRes.success) setRoles(rolesRes.roles);
    if (permsRes.success) setPermissions(permsRes.permissions);
  };

  const getAvailableRoles = () => {
    if (!user || !user.role) return roles;
    
    const currentLevel = adminAuthService.getRoleLevel(user.role);
    return roles.filter(role => {
      const roleLevel = adminAuthService.getRoleLevel(role.name);
      return roleLevel < currentLevel;
    });
  };

  const canManageUser = (targetUser) => {
    if (!user || !user.role) return false;
    
    const targetRoles = targetUser.roles || [];
    if (targetRoles.length === 0 && targetUser.admin_roles) {
      targetRoles.push(targetUser.admin_roles);
    }
    
    const targetLevel = Math.max(...targetRoles.map(r => adminAuthService.getRoleLevel(r.name || r)));
    const userLevel = adminAuthService.getRoleLevel(user.role);
    return userLevel > targetLevel;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleToggle = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(r => r !== roleId)
        : [...prev.roleIds, roleId]
    }));
  };

  const handlePermissionToggle = (permId) => {
    setFormData(prev => ({
      ...prev,
      customPermissions: prev.customPermissions.includes(permId)
        ? prev.customPermissions.filter(p => p !== permId)
        : [...prev.customPermissions, permId]
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!formData.email.trim() || formData.roleIds.length === 0) {
      setMessage({ type: 'error', text: 'Email and at least one role are required' });
      return;
    }

    const selectedRoles = roles.filter(r => formData.roleIds.includes(r.id));
    const lowestRoleId = formData.roleIds[0];

    for (const role of selectedRoles) {
      if (!canManageUser({ admin_roles: role })) {
        setMessage({ type: 'error', text: `You cannot create users with the ${role.name} role` });
        return;
      }
    }

    setLoading(true);

    const tempPassword = Math.random().toString(36).slice(-8);
    const additionalRoleIds = formData.roleIds.slice(1);

    const result = await adminAuthService.createAdminUser(
      formData.email,
      tempPassword,
      lowestRoleId,
      formData.customPermissions,
      additionalRoleIds
    );

    if (result.success) {
      setMessage({ 
        type: 'success', 
        text: `User created! Temporary password: ${tempPassword}` 
      });
      
      if (user) {
        await logsService.createLog(
          user.id,
          user.email,
          'USER_CREATE',
          {
            new_user_email: formData.email,
            roles_assigned: selectedRoles.map(r => r.name),
            has_custom_permissions: formData.customPermissions.length > 0
          }
        );
      }
      
      setFormData({ email: '', roleIds: [], customPermissions: [] });
      loadData();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleToggleActive = async (userId, isActive) => {
    const targetUser = users.find(u => u.id === userId);
    if (!canManageUser(targetUser)) {
      setMessage({ type: 'error', text: 'You do not have permission to manage this user' });
      return;
    }

    setLoading(true);

    const result = await adminAuthService.updateAdminUser(userId, {
      is_active: !isActive
    });

    if (result.success) {
      setMessage({ type: 'success', text: 'User status updated' });
      loadData();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    const targetUser = users.find(u => u.id === userId);
    if (!canManageUser(targetUser)) {
      setMessage({ type: 'error', text: 'You do not have permission to manage this user' });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) return;

    setLoading(true);

    const result = await adminAuthService.deleteAdminUser(userId);

    if (result.success) {
      setMessage({ type: 'success', text: 'User deleted' });
      
      if (user) {
        const deletedUser = users.find(u => u.id === userId);
        await logsService.createLog(
          user.id,
          user.email,
          'USER_DELETE',
          {
            deleted_user_id: userId,
            deleted_user_email: deletedUser?.email,
            deleted_user_roles: deletedUser?.roles?.map(r => r.name) || [deletedUser?.admin_roles?.name]
          }
        );
      }
      
      loadData();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleEditRoles = (userId) => {
    const targetUser = users.find(u => u.id === userId);
    if (!canManageUser(targetUser)) {
      setMessage({ type: 'error', text: 'You cannot manage this user' });
      return;
    }

    setEditingUserId(userId);
    setEditingUserRoleIds(targetUser.roles?.map(r => r.id) || []);
    setShowRoleModal(true);
  };

  const handleRoleToggleModal = (roleId) => {
    setEditingUserRoleIds(prev =>
      prev.includes(roleId)
        ? prev.filter(r => r !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSaveRoles = async () => {
    if (editingUserRoleIds.length === 0) {
      setMessage({ type: 'error', text: 'User must have at least one role' });
      return;
    }

    setLoading(true);

    const result = await adminAuthService.updateUserRoles(editingUserId, editingUserRoleIds);

    if (result.success) {
      setMessage({ type: 'success', text: 'User roles updated' });
      
      if (user) {
        const targetUser = users.find(u => u.id === editingUserId);
        const updatedRoles = editingUserRoleIds.map(rid => roles.find(r => r.id === rid)?.name).filter(Boolean);
        await logsService.createLog(
          user.id,
          user.email,
          'USER_ROLES_CHANGE',
          {
            updated_user_id: editingUserId,
            updated_user_email: targetUser?.email,
            new_roles: updatedRoles
          }
        );
      }
      
      setShowRoleModal(false);
      loadData();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleEditPermissions = async (userId) => {
    const targetUser = users.find(u => u.id === userId);
    if (!canManageUser(targetUser)) {
      setMessage({ type: 'error', text: 'You cannot manage this user' });
      return;
    }

    setEditingUserId(userId);
    const permsResult = await adminAuthService.getUserPermissions(userId);
    setEditingUserPermissions(permsResult);
    setShowPermissionModal(true);
  };

  const handlePermissionToggleModal = (permId) => {
    setEditingUserPermissions(prev =>
      prev.includes(permId)
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  const handleSavePermissions = async () => {
    setLoading(true);

    const result = await adminAuthService.updateUserPermissions(
      editingUserId,
      editingUserPermissions
    );

    if (result.success) {
      setMessage({ type: 'success', text: 'User permissions updated' });
      setShowPermissionModal(false);
      loadData();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleForcePasswordReset = async (userId) => {
    setLoading(true);

    const result = await adminAuthService.forcePasswordReset(userId, user.id);

    if (result.success) {
      setMessage({
        type: 'success',
        text: `Temporary password created: ${result.tempPassword}`
      });
      
      if (user) {
        const targetUser = users.find(u => u.id === userId);
        await logsService.createLog(
          user.id,
          user.email,
          'PASSWORD_RESET',
          {
            target_user_id: userId,
            target_user_email: targetUser?.email,
            action: 'forced_password_reset'
          }
        );
      }
      
      loadData();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const loggedInUserRole = user?.role;

  return (
    <div className="user-manager">
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Users List
        </button>
        <button
          className={`tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          Create User
        </button>
      </div>

      {activeTab === 'create' && (
        <form onSubmit={handleCreateUser} className="user-form">
          <h3>Create New Admin User</h3>

          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="user@example.com"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label>Assign Roles (Select one or more)</label>
            <div className="roles-grid">
              {getAvailableRoles().map(role => (
                <label key={role.id} className="role-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.roleIds.includes(role.id)}
                    onChange={() => handleRoleToggle(role.id)}
                    disabled={loading}
                  />
                  <span>
                    <strong>{role.name}</strong>
                    {role.description && <p className="role-desc">{role.description}</p>}
                  </span>
                </label>
              ))}
            </div>
            {getAvailableRoles().length === 0 && (
              <p className="info-text">No roles available to assign</p>
            )}
          </div>

          <div className="form-group">
            <label>Additional Permissions (Optional)</label>
            <div className="permissions-grid">
              {permissions.map(perm => (
                <label key={perm.id} className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.customPermissions.includes(perm.id)}
                    onChange={() => handlePermissionToggle(perm.id)}
                    disabled={loading}
                  />
                  <span>{perm.name}</span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Creating...' : 'Create User'}
          </button>
        </form>
      )}

      {activeTab === 'list' && (
        <div className="users-list">
          {users.length === 0 ? (
            <p className="no-users">No users found</p>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => {
                    const userRoles = user.roles && user.roles.length > 0 
                      ? user.roles 
                      : (user.admin_roles ? [user.admin_roles] : []);
                    
                    return (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td className="roles-cell">
                          <div className="roles-list">
                            {userRoles.map(role => (
                              <span key={role.id} className="role-badge">
                                {role.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</td>
                        <td>
                          <div className="actions">
                            {canManageUser(user) ? (
                              <>
                                <button
                                  onClick={() => handleEditRoles(user.id)}
                                  disabled={loading}
                                  className="edit-roles-button"
                                  title="Edit roles"
                                >
                                  Edit Roles
                                </button>
                                <button
                                  onClick={() => handleEditPermissions(user.id)}
                                  disabled={loading}
                                  className="edit-permissions-button"
                                  title="Edit permissions"
                                >
                                  Edit Permissions
                                </button>
                                <button
                                  onClick={() => handleToggleActive(user.id, user.is_active)}
                                  disabled={loading}
                                  className={`toggle-button ${user.is_active ? 'deactivate' : 'activate'}`}
                                  title={user.is_active ? 'Deactivate' : 'Activate'}
                                >
                                  {user.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                                {loggedInUserRole === 'Super Admin' && (
                                  <button
                                    onClick={() => handleForcePasswordReset(user.id)}
                                    className="reset-password-button"
                                    disabled={loading}
                                    title="Create temp password"
                                  >
                                    Reset Password
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteUser(user.id)}
                                  disabled={loading}
                                  className="delete-button"
                                  title="Delete user"
                                >
                                  Delete
                                </button>
                              </>
                            ) : (
                              <span className="no-permission-text">No permission to manage this user</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showRoleModal && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User Roles</h2>
              <button
                className="modal-close"
                onClick={() => setShowRoleModal(false)}
                disabled={loading}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-user-info">
                User: {users.find(u => u.id === editingUserId)?.email}
              </p>

              <div className="roles-grid-modal">
                {getAvailableRoles().map(role => (
                  <label key={role.id} className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={editingUserRoleIds.includes(role.id)}
                      onChange={() => handleRoleToggleModal(role.id)}
                      disabled={loading}
                    />
                    <span>
                      <strong>{role.name}</strong>
                      {role.description && <p className="role-desc">{role.description}</p>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowRoleModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleSaveRoles}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Roles'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPermissionModal && (
        <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User Permissions</h2>
              <button
                className="modal-close"
                onClick={() => setShowPermissionModal(false)}
                disabled={loading}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-user-info">
                User: {users.find(u => u.id === editingUserId)?.email}
              </p>

              <div className="permissions-grid-modal">
                {permissions.map(perm => (
                  <label key={perm.id} className="permission-checkbox">
                    <input
                      type="checkbox"
                      checked={editingUserPermissions?.includes(perm.id) || false}
                      onChange={() => handlePermissionToggleModal(perm.id)}
                      disabled={loading}
                    />
                    <span>
                      <strong>{perm.name}</strong>
                      {perm.description && <p className="perm-desc">{perm.description}</p>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowPermissionModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleSavePermissions}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
